import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  SelectChangeEvent,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { translate as t } from 'hooks/useTranslations';

dayjs.extend(customParseFormat);
import { useRiskPoolOrgContext } from '../../RiskPoolOrgContext';
import { usePolicyContext } from '../PolicyContext';
import usePolicyTrends, { getPeriodMode } from '../_data_sources/usePolicyTrends';
import type { PeriodMode } from '../_data_sources/usePolicyTrends';
import useAssignmentTrends from '../_data_sources/useAssignmentTrends';
import { neutral, warning, primary, error, info, highlight } from '_lex/_foundations/phxColors';
import OrganizationalTrendsFilters from './OrganizationalTrendsFilters';

type ViewMode = 'ack' | 'dtb';

interface OrganizationalTrendsProps {
  selectedManual: string;
  onManualChange: (event: SelectChangeEvent<string>) => void;
  onReportSelect: (reportType: string) => void;
}

export function OrganizationalTrends({ selectedManual, onManualChange, onReportSelect }: OrganizationalTrendsProps) {
  const { orgId, queryParams } = useRiskPoolOrgContext();
  const { manualId } = usePolicyContext();
  const [viewMode, setViewMode] = useState<ViewMode>('ack');

  const { data: policyData, isLoading: isPolicyLoading } = usePolicyTrends(orgId, {
    ...queryParams,
    manualId: manualId !== 'all' ? manualId : undefined,
  });
  const { data: completionData, isLoading: isCompletionLoading } = useAssignmentTrends(orgId, {
    ...queryParams,
    manualId: manualId !== 'all' ? manualId : undefined,
  });

  const pendingUpdatesData = policyData?.pendingUpdates ?? [];
  const completionAckData = completionData?.acknowledgements ?? [];
  const completionDtbData = completionData?.dtbs ?? [];
  const periodMode: PeriodMode = getPeriodMode(queryParams.startDate ?? '', queryParams.endDate ?? '');

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: ViewMode | null) => {
    if (newView !== null) setViewMode(newView);
  };

  // Card 1: Daily Assignment completions — ACK or DTB
  const activeCompletionData = viewMode === 'ack' ? completionAckData : completionDtbData;
  const completionXAxis = activeCompletionData.map((d) => d.date);
  const completionColor = viewMode === 'ack' ? highlight[500] : error[400];
  const completionSeriesLabel =
    viewMode === 'ack'
      ? t('dashboard.riskPool.riskPoolOrg.trends.completedAcknowledgements')
      : t('dashboard.riskPool.riskPoolOrg.trends.dtbCompletedOnTime');
  const hasCompletionData = activeCompletionData.some((d) => d.percent !== null);

  // Card 2: Pending Policy Updates
  const pendingXAxis = pendingUpdatesData.map((d) => d.date);
  const hasPendingData = pendingUpdatesData.length > 0;

  const allYears = new Set([...completionXAxis, ...pendingXAxis].map((d) => dayjs(d, 'YYYY-MM-DD', true).year()));
  const singleYear = allYears.size <= 1;

  const formatAxisPeriod = (period: string) => {
    const d = dayjs(period, 'YYYY-MM-DD', true);
    if (periodMode === 'weeks') return d.format(singleYear ? 'MM/DD' : 'MM/DD/YYYY');
    return d.format(singleYear ? 'MMM' : 'MMM YYYY');
  };

  const completionGradientId = `${viewMode}Gradient`;

  const CompletionAxisContent = useMemo(() => {
    const snap = activeCompletionData;
    const isAck = viewMode === 'ack';
    return function Content({ dataIndex }: { dataIndex?: number | null }) {
      if (dataIndex == null) return null;
      const point = snap[dataIndex];
      if (!point || point.percent === null) return null;
      const date = dayjs(point.date, 'YYYY-MM-DD', true).format('MMM D, YYYY');
      return (
        <Box sx={{ bgcolor: neutral[600], borderRadius: 2, px: 1.5, py: 1, minWidth: 220 }}>
          <Typography sx={{ fontSize: 14, color: 'white', fontWeight: 400, lineHeight: 1.5, mb: 0.5 }}>
            {date}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: neutral[50], lineHeight: 1.4 }}>
              {t('dashboard.riskPool.riskPoolOrg.trends.tooltipCompletionPct')}:
            </Typography>
            <Typography sx={{ fontSize: 12, color: neutral[50], lineHeight: 1.4 }}>
              {`${Math.round(point.percent * 100)}%`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: neutral[50], lineHeight: 1.4 }}>
              {isAck
                ? t('dashboard.riskPool.riskPoolOrg.trends.tooltipCompletedAck')
                : t('dashboard.riskPool.riskPoolOrg.trends.tooltipCompletedDtb')}
              :
            </Typography>
            <Typography sx={{ fontSize: 12, color: neutral[50], lineHeight: 1.4 }}>{point.count ?? 'N/A'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: neutral[50], lineHeight: 1.4 }}>
              {isAck
                ? t('dashboard.riskPool.riskPoolOrg.trends.tooltipTotalAck')
                : t('dashboard.riskPool.riskPoolOrg.trends.tooltipTotalDtb')}
              :
            </Typography>
            <Typography sx={{ fontSize: 12, color: neutral[50], lineHeight: 1.4 }}>
              {point.totalCount ?? 'N/A'}
            </Typography>
          </Box>
        </Box>
      );
    };
  }, [activeCompletionData, viewMode]);

  const PendingAxisContent = useMemo(() => {
    const snap = pendingUpdatesData;
    return function Content({ dataIndex }: { dataIndex?: number | null }) {
      if (dataIndex == null) return null;
      const point = snap[dataIndex];
      if (!point) return null;
      const date = dayjs(point.date, 'YYYY-MM-DD', true).format('MMM D, YYYY');
      return (
        <Box sx={{ bgcolor: neutral[600], borderRadius: 2, px: 1.5, py: 1, minWidth: 180 }}>
          <Typography sx={{ fontSize: 14, color: 'white', fontWeight: 400, lineHeight: 1.5, mb: 0.5 }}>
            {date}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: neutral[50], lineHeight: 1.4 }}>
              {t('dashboard.riskPool.riskPoolOrg.trends.tooltipPendingUpdates')}
            </Typography>
            <Typography sx={{ fontSize: 12, color: neutral[50], lineHeight: 1.4 }}>{point.count}</Typography>
          </Box>
        </Box>
      );
    };
  }, [pendingUpdatesData]);

  const renderCompletionChart = () => {
    if (isCompletionLoading) {
      return (
        <Box sx={{ width: '100%', height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!hasCompletionData) {
      return (
        <Box sx={{ width: '100%', height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant='body2' sx={{ color: neutral[400] }}>
            {t('dashboard.riskPool.riskPoolOrg.trends.noData') || 'No data available for the selected time range.'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', height: 240, position: 'relative' }}>
        <svg style={{ position: 'absolute', width: 0, height: 0 }}>
          <defs>
            <linearGradient id={completionGradientId} x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={completionColor} stopOpacity={0.3} />
              <stop offset='100%' stopColor={completionColor} stopOpacity={0.02} />
            </linearGradient>
          </defs>
        </svg>
        <LineChart
          xAxis={[
            {
              scaleType: 'point',
              data: completionXAxis,
              valueFormatter: (value: string) => formatAxisPeriod(value),
              tickNumber: Math.min(12, completionXAxis.length),
            },
          ]}
          yAxis={[
            {
              min: 0,
              max: 100,
              valueFormatter: (value: number) => `${value}%`,
            },
          ]}
          series={[
            {
              id: viewMode,
              label: completionSeriesLabel,
              data: activeCompletionData.map((d) => (d.percent !== null ? Math.round(d.percent * 100) : null)),
              color: completionColor,
              area: true,
              showMark: false,
            },
          ]}
          height={240}
          margin={{ top: 10, bottom: 30, left: 60, right: 10 }}
          slotProps={{ legend: { hidden: true } }}
          tooltip={{ trigger: 'axis' }}
          slots={{ axisContent: CompletionAxisContent }}
          sx={{
            '& .MuiLineElement-root': { strokeWidth: 2 },
            [`& .MuiAreaElement-series-${viewMode}`]: { fill: `url(#${completionGradientId})` },
            '& .MuiChartsTooltip-paper': { bgcolor: 'transparent', boxShadow: 'none', px: 3, py: 2 },
          }}
        />

      </Box>
    );
  };

  const renderPendingChart = () => {
    if (isPolicyLoading) {
      return (
        <Box sx={{ width: '100%', height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!hasPendingData) {
      return (
        <Box sx={{ width: '100%', height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant='body2' sx={{ color: neutral[400] }}>
            {t('dashboard.riskPool.riskPoolOrg.trends.noData') || 'No data available for the selected time range.'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', height: 240, position: 'relative' }}>
        <svg style={{ position: 'absolute', width: 0, height: 0 }}>
          <defs>
            <linearGradient id='pendingGradient' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={info[500]} stopOpacity={0.3} />
              <stop offset='100%' stopColor={info[500]} stopOpacity={0.02} />
            </linearGradient>
          </defs>
        </svg>
        <LineChart
          xAxis={[
            {
              scaleType: 'point',
              data: pendingXAxis,
              valueFormatter: (value: string) => formatAxisPeriod(value),
              tickNumber: Math.min(12, pendingXAxis.length),
            },
          ]}
          yAxis={[{ min: 0 }]}
          series={[
            {
              id: 'pending',
              label: t('dashboard.riskPool.riskPoolOrg.trends.pendingUpdates'),
              data: pendingUpdatesData.map((d) => d.count),
              color: info[500],
              area: true,
              showMark: false,
            },
          ]}
          height={240}
          margin={{ top: 10, bottom: 30, left: 60, right: 10 }}
          slotProps={{ legend: { hidden: true } }}
          tooltip={{ trigger: 'axis' }}
          slots={{ axisContent: PendingAxisContent }}
          sx={{
            '& .MuiLineElement-root': { strokeWidth: 2 },
            '& .MuiAreaElement-series-pending': { fill: 'url(#pendingGradient)' },
            '& .MuiChartsTooltip-paper': { bgcolor: 'transparent', boxShadow: 'none', px: 3, py: 2 },
          }}
        />
      </Box>
    );
  };

  return (
    <Box>
      {/* Header row: "Organizational Trends" title + filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='subtitleLg'>{t('dashboard.riskPool.riskPoolOrg.trends.title')}</Typography>
        <OrganizationalTrendsFilters
          selectedManual={selectedManual}
          onManualChange={onManualChange}
          onReportSelect={onReportSelect}
        />
      </Box>

      {/* Card 1: Daily Assignment completions */}
      <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}`, mb: 3 }}>
        <CardHeader
          title={t('dashboard.riskPool.riskPoolOrg.trends.dailyAssignmentCompletions')}
          subheader={t('dashboard.riskPool.riskPoolOrg.trends.dailyAssignmentSubtitle')}
          action={
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              sx={{
                borderRadius: 2,
                border: `1px solid ${neutral[200]}`,
                overflow: 'hidden',
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 1,
                  border: 'none',
                  borderRight: `1px solid ${neutral[200]}`,
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                  color: neutral[800],
                  textTransform: 'none',
                  '&:last-of-type': { borderRight: 'none' },
                  '&.Mui-selected': { backgroundColor: primary[50], color: neutral[800] },
                  '&:not(.Mui-selected)': { backgroundColor: 'white' },
                },
              }}
            >
              <ToggleButton value='ack'>{t('dashboard.riskPool.riskPoolOrg.trends.ackTab')}</ToggleButton>
              <ToggleButton value='dtb'>{t('dashboard.riskPool.riskPoolOrg.trends.dtbTab')}</ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <CardContent>{renderCompletionChart()}</CardContent>
      </Card>

      {/* Card 2: Pending Policy Updates */}
      <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}` }}>
        <CardHeader
          title={t('dashboard.riskPool.riskPoolOrg.trends.pendingPolicyUpdatesTitle')}
          subheader={t('dashboard.riskPool.riskPoolOrg.trends.pendingPolicyUpdatesSubtitle')}
        />
        <CardContent>{renderPendingChart()}</CardContent>
      </Card>
    </Box>
  );
}

export default OrganizationalTrends;
